export interface SearchResult {
  id: string;
  title: string;
  href: string;
  category: string;
  content: string;
  keywords?: string[];
}

export const searchIndex: SearchResult[] = [
  {
    id: "intro",
    title: "Introduction",
    href: "/dashboard/onboarding-documentation",
    category: "General",
    content: "Overview of the Enterprise Onboarding Guide and Platform Tiers. Core focus on technical fidelity and business logic for testing engagements. Comprehensive guide for enterprise security teams.",
    keywords: ["welcome", "start", "guide", "enterprise", "tiers", "intro", "onboarding"]
  },
  {
    id: "step1-main",
    title: "Asset Inventory",
    href: "/dashboard/onboarding-documentation/asset-inventory",
    category: "Step 1",
    content: "Register and classify digital assets. Identity, lifecycle, and criticality management for corporate assets, targets, and infrastructure.",
    keywords: ["assets", "inventory", "registration", "classification", "registry", "asset"]
  },
  {
    id: "step1-identity",
    title: "Identity & Ownership",
    href: "/dashboard/onboarding-documentation/asset-inventory#identity-ownership",
    category: "Step 1",
    content: "Establish primary identifiers, Risk Owner, and Technical Owner for accountability and security governance.",
    keywords: ["owner", "risk", "accountability", "technical", "identity", "owners"]
  },
  {
    id: "step1-lifecycle",
    title: "Lifecycle & Usage",
    href: "/dashboard/onboarding-documentation/asset-inventory#lifecycle-usage",
    category: "Step 1",
    content: "Asset status (Active, Maintenance), change frequency, and deployment models like SaaS and On-Prem.",
    keywords: ["status", "active", "maintenance", "deprecated", "saas", "on-prem", "lifecycle"]
  },
  {
    id: "step1-classification",
    title: "Classification & Criticality",
    href: "/dashboard/onboarding-documentation/asset-inventory#classification-criticality",
    category: "Step 1",
    content: "Digital Apps, Infra, Identity, Code, and business impact levels from Critical to Low. Risk-based prioritization.",
    keywords: ["impact", "critical", "risk", "apps", "infrastructure", "cloud", "classification"]
  },
  {
    id: "step1-data",
    title: "Data & Privacy",
    href: "/dashboard/onboarding-documentation/asset-inventory#data-privacy",
    category: "Step 1",
    content: "Data classification for Regulated data and PII. Compliance with GDPR, HIPAA, and PCI residency requirements.",
    keywords: ["privacy", "gdpr", "hipaa", "pci", "regulated", "residency", "data"]
  },
  {
    id: "step1-access",
    title: "Access Control",
    href: "/dashboard/onboarding-documentation/asset-inventory#access-control",
    category: "Step 1",
    content: "Authentication requirements, RBAC/ABAC models, and Identity Providers like Okta and Azure SSO.",
    keywords: ["auth", "rbac", "abac", "okta", "azure", "sso", "login", "access"]
  },
  {
    id: "step1-testing",
    title: "Security Testing Focus",
    href: "/dashboard/onboarding-documentation/asset-inventory#security-testing",
    category: "Step 1",
    content: "Internet exposure, testing focus (Web, API, Mobile, Pentest), and Rules of Engagement (ROE).",
    keywords: ["exposure", "web", "api", "mobile", "pentest", "roe", "testing"]
  },
  {
    id: "step1-governance",
    title: "Governance & Attestation",
    href: "/dashboard/onboarding-documentation/asset-inventory#governance-attestation",
    category: "Step 1",
    content: "Legal rights, authorization, and digital signature for SOC2 audit compliance and attestation.",
    keywords: ["legal", "authorization", "rights", "soc2", "audit", "signature", "governance"]
  },
  {
    id: "step2-main",
    title: "Scoping & Targets",
    href: "/dashboard/onboarding-documentation/scoping",
    category: "Step 2",
    content: "Define technical boundaries. URL, API, Mobile App, CIDR, and Cloud Resource targets for testing.",
    keywords: ["scope", "targets", "boundary", "url", "cidr", "cloud", "target"]
  },
  {
    id: "step2-mappings",
    title: "Asset-to-Target Mappings",
    href: "/dashboard/onboarding-documentation/scoping#asset-mappings",
    category: "Step 2",
    content: "Recommended target templates for Web, API, Mobile, Infra, and Cloud assets. Mapping for internet exposure.",
    keywords: ["mapping", "template", "recommendation", "type", "mappings"]
  },
  {
    id: "step2-handshake",
    title: "Safety & Compliance Handshake",
    href: "/dashboard/onboarding-documentation/scoping#safety-handshake",
    category: "Step 2",
    content: "Mandatory Scope Authorization and Operational Safety checkpoints for compliant testing.",
    keywords: ["safety", "compliance", "handshake", "authorization", "audit"]
  },
  {
    id: "step3-main",
    title: "Endpoint Discovery",
    href: "/dashboard/onboarding-documentation/endpoint-discovery",
    category: "Step 3",
    content: "Catalog technical resources: Paths, IPs, Ports, Parameters, and Files. Bulk Import and automated endpoint discovery from target assets.",
    keywords: ["endpoint", "endpoints", "discovery", "catalog", "path", "port", "ip", "bulk", "import", "normalization"]
  },
  {
    id: "step3-resource",
    title: "Resource Specification",
    href: "/dashboard/onboarding-documentation/endpoint-discovery#resource-types",
    category: "Step 3",
    content: "Deep scoping for Web Pages, API routes, IP addresses, and custom resources. Detailed endpoint metrics.",
    keywords: ["specification", "port", "parameter", "header", "file", "resource"]
  },
  {
    id: "step3-normalization",
    title: "Normalization & Validation",
    href: "/dashboard/onboarding-documentation/endpoint-discovery#normalization",
    category: "Step 3",
    content: "Automated case-insensitivity, duplicate suppression, and syntax guards for endpoint imports.",
    keywords: ["normalization", "validation", "duplicate", "syntax", "import", "discovery"]
  },
  {
    id: "step4-main",
    title: "Credential Vault & Flow",
    href: "/dashboard/onboarding-documentation/credential-vault",
    category: "Step 4",
    content: "Zero-knowledge encryption for passwords, API keys, and certificates. Secure vault access control for researcher credential sharing.",
    keywords: ["vault", "vaults", "credentials", "passwords", "keys", "secrets", "encryption", "sharing", "reveal"]
  },
  {
    id: "step4-lifecycle",
    title: "Vault Access Lifecycle",
    href: "/dashboard/onboarding-documentation/credential-vault#vault-lifecycle",
    category: "Step 4",
    content: "The 8-step flow from creation to secure researcher reveal using encrypted tokens.",
    keywords: ["lifecycle", "reveal", "auth", "token", "credentials"]
  },
  {
    id: "step4-passwords",
    title: "Master vs Access Passwords",
    href: "/dashboard/onboarding-documentation/credential-vault#security-concepts",
    category: "Step 4",
    content: "Understanding private zero-knowledge keys vs. shareable researcher passwords for the vault.",
    keywords: ["master", "access", "manager", "sharing", "password"]
  },
  {
    id: "step4-faq",
    title: "Vault FAQ & Revocation",
    href: "/dashboard/onboarding-documentation/credential-vault#faq",
    category: "Step 4",
    content: "Handling forgotten passwords and instant access revocation for secure credentials.",
    keywords: ["faq", "help", "forget", "revoke", "unlink", "troubleshoot", "vault"]
  },
  {
    id: "step5-main",
    title: "Program Deployment & Wizard",
    href: "/dashboard/onboarding-documentation/program-engineering",
    category: "Step 5",
    content: "The 7-step wizard linking assets, scope, and credentials for testing. Program deployment automation.",
    keywords: ["program", "programs", "deployment", "wizard", "launch", "setup"]
  },
  {
    id: "step5-scheduling",
    title: "Scheduling Patterns & Tiers",
    href: "/dashboard/onboarding-documentation/program-engineering#scheduling",
    category: "Step 5",
    content: "Hard-coded restrictions for One-off (Pilot) vs Continuous (Enterprise) subscriptions. Pentest execution scheduling.",
    keywords: ["scheduling", "tiers", "subscription", "one-off", "continuous", "pilot", "pentest", "execution"]
  },
  {
    id: "step5-conflicts",
    title: "Conflict Detection (Occupied)",
    href: "/dashboard/onboarding-documentation/program-engineering#safety-checks",
    category: "Step 5",
    content: "Understanding 'Occupied in timeframe' and 'Already in Cycle' target statuses to prevent scheduling overlaps and resource conflicts.",
    keywords: ["conflict", "occupied", "overlap", "check", "timeframe", "conflicts"]
  },
  {
    id: "step5-draft",
    title: "Draft Recovery & Persistence",
    href: "/dashboard/onboarding-documentation/program-engineering#safety-checks",
    category: "Step 5",
    content: "Automated localStorage-based progress persistence for the deployment wizard and program drafts.",
    keywords: ["draft", "persistence", "recovery", "progress", "storage", "drafts"]
  },
  {
    id: "step6-main",
    title: "Scheduled Runs & Quota",
    href: "/dashboard/onboarding-documentation/scheduled-runs",
    category: "Step 6",
    content: "Managing testing windows, quota units, and the run lifecycle. Quota Monitoring for real-time balance tracking, depletion dates, and historical run costs. Managed Engagements for One-off Pentest Reports.",
    keywords: ["runs", "quota", "units", "window", "maintenance", "monitoring", "report", "reports", "pentest", "findings", "overage"]
  },
  {
    id: "step6-oneoff",
    title: "One-off Managed Engagements",
    href: "/dashboard/onboarding-documentation/scheduled-runs#run-types",
    category: "Step 6",
    content: "Bypassing manual scheduling for Pilot and One-off program tiers. Automated execution for pentest reports and certificates.",
    keywords: ["one-off", "pilot", "managed", "automatic", "reports"]
  },
  {
    id: "step6-lifecycle",
    title: "Run Lifecycle (Pause/Resume)",
    href: "/dashboard/onboarding-documentation/scheduled-runs#lifecycle",
    category: "Step 6",
    content: "Operational states including Scheduled, Active, Paused, and Completed. Lifecycle management for active testing runs.",
    keywords: ["lifecycle", "pause", "resume", "operational", "states", "runs"]
  },
  {
    id: "step7-main",
    title: "Snapshot Verification Handshake",
    href: "/dashboard/onboarding-documentation/snapshot-verification",
    category: "Step 7",
    content: "The 3-state technical sign-off: Draft -> Pending -> Verified. Snapshot verification forms the base of the final Pentest Report, reports, and technical audit artifacts.",
    keywords: ["snapshot", "verification", "handshake", "sign-off", "audit", "pentest", "report", "reports", "findings", "cert", "reporting"]
  },
  {
    id: "step7-immutability",
    title: "Scope Immutability & Compliance",
    href: "/dashboard/onboarding-documentation/snapshot-verification#guidance",
    category: "Step 7",
    content: "Why scope locks after verification to fulfill SOC2 Type II and ISO 27001 audit requirements for point-in-time Pentest Reports. Immutability ensures audit integrity.",
    keywords: ["immutability", "compliance", "soc2", "iso", "locked", "audit", "report", "reports"]
  },
  {
    id: "step7-pull",
    title: "Pull Inventory Sync",
    href: "/dashboard/onboarding-documentation/snapshot-verification#guidance",
    category: "Step 7",
    content: "Synchronizing newly discovered endpoints into an active testing snapshot. Pull inventory action for draft scope updates.",
    keywords: ["pull", "sync", "inventory", "discovery", "endpoints", "snapshot"]
  }
];
